// components/Home.tsx
import React from "react";
import { Card, Col, Row, Button, Typography, List, Tag } from "antd";
import {
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Candidate, Role, getStatusColor } from "../types";

const { Title, Text } = Typography;

interface HomeProps {
  candidates: Candidate[];
  roles: Role[];
  onViewCandidate: (candidate: Candidate) => void;
}

export const Home: React.FC<HomeProps> = ({
  candidates,
  roles,
  onViewCandidate,
}) => {
  const navigate = useNavigate();

  // --- Calculate Stats ---
  const totalCandidates = candidates.length;
  const activeCandidates = candidates.filter((c) =>
    ["Applied", "Screening", "Interview", "Offer"].includes(c.status)
  ).length;
  const hiredCandidates = candidates.filter((c) => c.status === "Hired").length;
  const openRoles = roles.length;

  const recentCandidates = [...candidates]
    .sort(
      (a, b) =>
        new Date(b.appliedDate).getTime() - new Date(a.appliedDate).getTime()
    )
    .slice(0, 5);

  // Helper for Stats Cards
  const StatCard = ({ title, value, icon, color, bg }: any) => (
    <Card
      bordered={false}
      className="hover-card"
      style={{
        borderRadius: 16,
        height: "100%",
        boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <Text type="secondary" style={{ fontSize: 14, fontWeight: 500 }}>
            {title}
          </Text>
          <div style={{ fontSize: 32, fontWeight: 700, marginTop: 4 }}>
            {value}
          </div>
        </div>
        <div
          style={{
            background: bg,
            color: color,
            width: 48,
            height: 48,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      {/* Hero Section */}
      <div style={{ marginBottom: 40, textAlign: "left" }}>
        <Title level={2} style={{ marginBottom: 8 }}>
          Hello, Recruiter! 👋
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Here is what's happening in your hiring pipeline today.
        </Text>
      </div>

      {/* Stats Row */}
      <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Total Candidates"
            value={totalCandidates}
            icon={<TeamOutlined />}
            color="#4f46e5"
            bg="#eef2ff"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Active Process"
            value={activeCandidates}
            icon={<ClockCircleOutlined />}
            color="#f59e0b"
            bg="#fffbeb"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Hired"
            value={hiredCandidates}
            icon={<CheckCircleOutlined />}
            color="#10b981"
            bg="#ecfdf5"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Open Roles"
            value={openRoles}
            icon={<ThunderboltFilled />}
            color="#ec4899"
            bg="#fdf2f8"
          />
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Quick Actions */}
        <Col xs={24} md={10}>
          <Card
            title="Quick Actions"
            bordered={false}
            className="hover-card"
            style={{
              borderRadius: 16,
              height: "100%",
              boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Button
                type="primary"
                size="large"
                icon={<TeamOutlined />}
                onClick={() => navigate("/candidates")}
                block
                style={{ height: 56, borderRadius: 12, fontSize: 16 }}
              >
                Manage Candidates
              </Button>
              <Button
                size="large"
                icon={<UserOutlined />}
                onClick={() => navigate("/roles")}
                block
                style={{ height: 56, borderRadius: 12, fontSize: 16 }}
              >
                Manage Job Roles
              </Button>
            </div>
          </Card>
        </Col>

        {/* Recent Activity */}
        <Col xs={24} md={14}>
          <Card
            title="Recent Applications"
            bordered={false}
            className="hover-card"
            style={{
              borderRadius: 16,
              height: "100%",
              boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
            }}
            extra={
              recentCandidates.length > 0 && (
                <Button type="link" onClick={() => navigate("/candidates")}>
                  View All
                </Button>
              )
            }
          >
            <List
              itemLayout="horizontal"
              dataSource={recentCandidates}
              renderItem={(item) => (
                <List.Item
                  style={{
                    padding: "16px 0",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          background: "#f1f5f9",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#64748b",
                        }}
                      >
                        <UserOutlined />
                      </div>
                    }
                    title={
                      <Button
                        onClick={() => onViewCandidate(item)}
                        style={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: 15,
                        }}
                      >
                        {item.name}
                      </Button>
                    }
                    description={
                      <span>
                        Applied for <Text strong>{item.role}</Text>
                      </span>
                    }
                  />
                  <div style={{ textAlign: "right" }}>
                    <Tag
                      color={getStatusColor(item.status)}
                      style={{ borderRadius: 12, marginRight: 0 }}
                    >
                      {item.status}
                    </Tag>
                    <div
                      style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}
                    >
                      {item.appliedDate}
                    </div>
                  </div>
                </List.Item>
              )}
            />
            {recentCandidates.length === 0 && (
              <div style={{ textAlign: "center", padding: 20 }}>
                <Text type="secondary">No candidates yet. Start hiring!</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
